import confluenceIcon from "@/assets/icons/confluence.png";
import jiraIcon from "@/assets/icons/jira.png";
import slackIcon from "@/assets/icons/slack.png";
import { Badge } from "@/components/badge";
import { Editor } from "@/components/editor";
import {
  useAudienceAndCampaignOptions,
  useExperimentCampaigns,
  usePredictionFlightOptions,
  useUsers,
} from "@/http/api-hooks";
import {
  CampaignParam,
  Experiment,
  ExperimentImportedPredictionFlight,
  ExperimentImportedPredictorConfig,
  ExperimentType,
  ExperimentVariant,
  PredictionFlightParam,
} from "@/http/api-types";
import { predictorTypeName } from "@/lib/predictor-types";
import { cn, formatUTCDateTime, getAudienceSelectOptions } from "@/lib/utils";
import { ChevronRight, Globe } from "lucide-react";
import { useMemo } from "react";

type SetupTabProps = {
  experiment: Experiment;
};

type DisplayParam = {
  key: string;
  param_value: string;
};

type VariantFlowItem = {
  key: string;
  title: string;
  subtitle?: string;
  campaignID?: number;
  controlCampaignID?: number;
  testGroup?: number;
  variant: ExperimentVariant;
  trafficAllocation: number;
  params?: DisplayParam[];
  predictorSources?: ExperimentImportedPredictorConfig[];
};

function toCampaignDisplayParams(params: CampaignParam[] | undefined): DisplayParam[] {
  return (params || []).map(p => ({ key: p.param_name, param_value: p.param_value }));
}

function toPredictionFlightDisplayParams(params: PredictionFlightParam[] | undefined): DisplayParam[] {
  return (params || []).map(p => ({
    key: predictorTypeName(p.predictor_type_id),
    param_value: p.param_value,
  }));
}

type CustomCampaignGroup = {
  key: string;
  control: VariantFlowItem;
  tests: VariantFlowItem[];
};

type LinkIconKind = "jira" | "confluence" | "slack" | "generic";

function formatAllocation(trafficAllocation: number) {
  return `${(trafficAllocation / 100).toFixed(2)}%`;
}

function formatParamValueForDisplay(value: string) {
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === "object") {
      return {
        isJSON: true,
        value: JSON.stringify(parsed, null, 2),
      };
    }
  } catch {
    return {
      isJSON: false,
      value,
    };
  }

  return {
    isJSON: false,
    value,
  };
}

function getLinkIconKind(url: string): LinkIconKind {
  const normalizedUrl = url.trim().toLowerCase();
  const jiraPrefixes = ["https://remerge.atlassian.net/browse/", "https://remerge.atlassian.net/jira/"];

  if (jiraPrefixes.some(prefix => normalizedUrl.includes(prefix))) {
    return "jira";
  }

  if (normalizedUrl.includes("https://remerge.atlassian.net/wiki")) {
    return "confluence";
  }

  if (normalizedUrl.startsWith("https://remerge.slack.com")) {
    return "slack";
  }

  return "generic";
}

function LinkIcon({ kind }: { kind: LinkIconKind }) {
  if (kind === "jira") {
    return <img src={jiraIcon} alt="" className="h-5 w-5 shrink-0 object-contain" aria-hidden="true" />;
  }

  if (kind === "confluence") {
    return <img src={confluenceIcon} alt="" className="h-5 w-5 shrink-0 object-contain" aria-hidden="true" />;
  }

  if (kind === "slack") {
    return <img src={slackIcon} alt="" className="h-5 w-5 shrink-0 object-contain" aria-hidden="true" />;
  }

  return <Globe className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />;
}

function VariantParams({ item, className }: { item: VariantFlowItem; className?: string }) {
  if (!item.params || item.params.length === 0) return null;

  return (
    <div className={cn("grid gap-2", className)}>
      {item.params.map((param, index) => {
        const formattedParam = formatParamValueForDisplay(param.param_value);

        if (!formattedParam.isJSON) {
          return (
            <div key={`${item.key}-${index}`} className="variant-flow-param-list">
              <span className="variant-flow-param-chip">
                {param.key}={formattedParam.value}
              </span>
            </div>
          );
        }

        return (
          <div key={`${item.key}-${index}`} className="rounded-md bg-muted/40 px-3 py-2 text-xs text-foreground">
            <div className="mb-2 font-medium">{param.key}</div>
            <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs text-muted-foreground">
              {formattedParam.value}
            </pre>
          </div>
        );
      })}
    </div>
  );
}

function PredictorSources({ item, className }: { item: VariantFlowItem; className?: string }) {
  if (!item.predictorSources || item.predictorSources.length === 0) return null;

  return (
    <div className={cn("grid gap-1 text-xs text-muted-foreground", className)}>
      {item.predictorSources.map((predictorConfig, index) => (
        <div key={`${item.key}-predictor-source-${index}`} className="break-words">
          <span className="font-medium text-foreground">{predictorTypeName(predictorConfig.predictor_type)}</span>
          {": "}
          Repo: {predictorConfig.repo?.trim() || "not set"} · Branch: {predictorConfig.branch?.trim() || "not set"}
        </div>
      ))}
    </div>
  );
}

function variantDetailsSummary(item: VariantFlowItem) {
  const paramCount = item.params?.length || 0;
  const predictorSourceCount = item.predictorSources?.length || 0;
  if (paramCount === 0 && predictorSourceCount === 0) {
    return "-";
  }

  return [
    paramCount > 0 ? `${paramCount} ${paramCount === 1 ? "param" : "params"}` : "",
    predictorSourceCount > 0 ? `${predictorSourceCount} ${predictorSourceCount === 1 ? "source" : "sources"}` : "",
  ]
    .filter(Boolean)
    .join(", ");
}

function hasVariantDetails(item: VariantFlowItem) {
  return Boolean(item.params?.length || item.predictorSources?.length);
}

function VariantDetailsIndicator({ isExpandable, label }: { isExpandable: boolean; label: string }) {
  return (
    <div className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
      {isExpandable && (
        <ChevronRight className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />
      )}
      <span className="truncate">{label}</span>
    </div>
  );
}

function VariantExpandedDetails({ item }: { item: VariantFlowItem }) {
  if (!hasVariantDetails(item)) return null;

  const summary = variantDetailsSummary(item);
  return (
    <div className="rounded-md border border-border border-l-4 bg-muted/20 px-3 py-2 text-xs">
      <div className="inline-flex flex-wrap items-center gap-2">
        <span className="font-medium text-muted-foreground">Details for</span>
        <Badge variant={item.variant} className="px-1.5 py-0 text-[10px]">
          {item.variant === "control" ? "Control" : "Test"}
        </Badge>
        <span className="font-medium text-foreground">{item.title}</span>
        <span className="text-muted-foreground">({summary})</span>
      </div>
      <div className="mt-3 grid gap-2">
        <VariantParams item={item} />
        <PredictorSources item={item} />
      </div>
    </div>
  );
}

function VariantOverviewTable({ items }: { items: VariantFlowItem[] }) {
  const orderedItems = [...items].sort((left, right) => {
    if (left.variant !== right.variant) return left.variant === "control" ? -1 : 1;
    return left.title.localeCompare(right.title);
  });

  return (
    <div className="text-sm">
      <div className="grid grid-cols-[84px_minmax(0,1fr)_88px_112px] gap-3 border-b border-border px-3 py-2 text-sm font-medium text-muted-foreground sm:grid-cols-[96px_minmax(0,1fr)_96px_128px]">
        <div>Variant</div>
        <div>Member</div>
        <div className="text-right">Traffic</div>
        <div>Details</div>
      </div>
      <div className="divide-y divide-border">
        {orderedItems.map(item => {
          const itemHasDetails = hasVariantDetails(item);
          const rowContent = (
            <div className="grid grid-cols-[84px_minmax(0,1fr)_88px_112px] items-center gap-3 px-3 py-3 sm:grid-cols-[96px_minmax(0,1fr)_96px_128px]">
              <div>
                <Badge variant={item.variant}>{item.variant === "control" ? "Control" : "Test"}</Badge>
              </div>
              <div className="min-w-0">
                <div className="truncate font-medium">{item.title}</div>
                {item.subtitle && <div className="text-xs text-muted-foreground">{item.subtitle}</div>}
              </div>
              <div className="text-right font-medium">{formatAllocation(item.trafficAllocation)}</div>
              <VariantDetailsIndicator isExpandable={itemHasDetails} label={variantDetailsSummary(item)} />
            </div>
          );

          if (!itemHasDetails) {
            return <div key={item.key}>{rowContent}</div>;
          }

          return (
            <details key={item.key} className="group [&>summary::-webkit-details-marker]:hidden">
              <summary className="cursor-pointer list-none hover:bg-muted/40">{rowContent}</summary>
              <div className="px-3 pb-3">
                <VariantExpandedDetails item={item} />
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
}

function CustomCampaignGroups({
  groups,
  unassignedTests,
}: {
  groups: CustomCampaignGroup[];
  unassignedTests: VariantFlowItem[];
}) {
  const rows = [
    ...groups.map(group => ({
      key: group.key,
      control: group.control,
      tests: group.tests,
      isUnassigned: false,
    })),
    ...(unassignedTests.length > 0
      ? [
          {
            key: "unassigned-tests",
            control: undefined,
            tests: unassignedTests,
            isUnassigned: true,
          },
        ]
      : []),
  ];

  return (
    <div className="text-sm">
      <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.25fr)_88px_112px] gap-3 border-b border-border px-3 py-2 text-sm font-medium text-muted-foreground sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.3fr)_96px_120px]">
        <div>Control</div>
        <div>Tests</div>
        <div className="text-right">Traffic</div>
        <div>Details</div>
      </div>
      <div className="divide-y divide-border">
        {rows.map(row => {
          const groupTotal =
            (row.control?.trafficAllocation || 0) + row.tests.reduce((sum, test) => sum + test.trafficAllocation, 0);
          const detailItems = [row.control, ...row.tests].filter((item): item is VariantFlowItem =>
            Boolean(item && hasVariantDetails(item)),
          );
          const detailSummary =
            detailItems.length > 0 ? `${detailItems.length} ${detailItems.length === 1 ? "detail" : "details"}` : "-";

          const rowContent = (
            <div
              className={cn(
                "grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.25fr)_88px_112px] items-center gap-3 px-3 py-3 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.3fr)_96px_120px]",
                row.isUnassigned && "bg-amber-50/50",
              )}
            >
              <div className="min-w-0">
                {row.control ? (
                  <>
                    <div className="flex min-w-0 items-center gap-2">
                      <Badge variant="control">Control</Badge>
                      <span className="min-w-0 truncate font-medium">{row.control.title}</span>
                    </div>
                    {row.control.subtitle && (
                      <div className="text-xs text-muted-foreground">{row.control.subtitle}</div>
                    )}
                  </>
                ) : (
                  <span className="text-sm font-medium text-amber-900">Unassigned</span>
                )}
              </div>
              <div className="min-w-0">
                {row.tests.length > 0 ? (
                  <div className="grid gap-1.5">
                    {row.tests.map(test => (
                      <div key={test.key} className="min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                          <Badge variant="test">Test</Badge>
                          {test.testGroup ? (
                            <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-[10px] text-muted-foreground">
                              Group {test.testGroup}
                            </Badge>
                          ) : null}
                          <span className="min-w-0 truncate font-medium">{test.title}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {formatAllocation(test.trafficAllocation)}
                          </span>
                        </div>
                        {test.subtitle && <div className="text-xs text-muted-foreground">{test.subtitle}</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">No tests</span>
                )}
              </div>
              <div className="text-right font-medium">{formatAllocation(groupTotal)}</div>
              <VariantDetailsIndicator isExpandable={detailItems.length > 0} label={detailSummary} />
            </div>
          );

          if (detailItems.length === 0) {
            return <div key={row.key}>{rowContent}</div>;
          }

          return (
            <details key={row.key} className="group [&>summary::-webkit-details-marker]:hidden">
              <summary className="cursor-pointer list-none hover:bg-muted/40">{rowContent}</summary>
              <div className="grid gap-2 px-3 pb-3">
                {detailItems.map(item => (
                  <VariantExpandedDetails key={`${row.key}-details-${item.key}`} item={item} />
                ))}
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
}

export function SetupTab({ experiment }: SetupTabProps) {
  const experimentId = experiment.id ?? null;
  const externalLinks = (experiment.external_links || []).filter(link => link.trim() !== "");

  // Fetch supporting data for lookups
  const { data: users } = useUsers();
  const { data: campaigns } = useExperimentCampaigns(experimentId);
  const { data: audienceCampaignData } = useAudienceAndCampaignOptions({
    campaign_type: "all",
  });
  const { data: predictionFlightsData } = usePredictionFlightOptions();

  // Create lookup maps for display
  const userMap = useMemo(() => {
    const map: Record<number, string> = {};
    if (users) {
      users.forEach(user => {
        map[user.id] = user.name;
      });
    }
    return map;
  }, [users]);

  const campaignNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (audienceCampaignData) {
      const audienceOptions = getAudienceSelectOptions(audienceCampaignData);
      if (audienceOptions) {
        Object.values(audienceOptions).forEach(audience => {
          audience.campaigns.forEach(campaign => {
            map[campaign.id.toString()] = campaign.name;
          });
        });
      }
    }
    return map;
  }, [audienceCampaignData]);

  const audienceNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (audienceCampaignData) {
      const audienceOptions = getAudienceSelectOptions(audienceCampaignData);
      if (audienceOptions) {
        Object.keys(audienceOptions).forEach(audienceId => {
          map[audienceId] = audienceOptions[audienceId].audience_name;
        });
      }
    }
    return map;
  }, [audienceCampaignData]);

  const predictionFlightNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (predictionFlightsData?.results) {
      predictionFlightsData.results.forEach(pf => {
        map[pf.id.toString()] = pf.name;
      });
    }
    experiment.import_metadata?.prediction_flights?.forEach(predictionFlight => {
      map[predictionFlight.prediction_flight_id.toString()] = predictionFlight.name;
    });
    return map;
  }, [experiment.import_metadata?.prediction_flights, predictionFlightsData]);

  const importedPredictionFlightMetadataMap = useMemo(() => {
    const map: Record<string, ExperimentImportedPredictionFlight> = {};
    experiment.import_metadata?.prediction_flights?.forEach(predictionFlight => {
      map[predictionFlight.prediction_flight_id.toString()] = predictionFlight;
    });
    return map;
  }, [experiment.import_metadata?.prediction_flights]);

  const scopeSummary = (experiment.scope_filters || [])
    .map(scopeFilter => {
      switch (scopeFilter.kind) {
        case "audience_id":
          return `Audience ${scopeFilter.value}`;
        case "campaign_id":
          return `Campaign ${scopeFilter.value}`;
        case "campaign_type":
          return `Campaign Type ${scopeFilter.value}`;
        case "budget_objective": {
          const budgetObjectiveLabels: Record<string, string> = {
            performance: "Performance",
            spend_with_perf_nudging: "Auto-Spend",
            other: "Other",
          };
          return `Budget Objective: ${budgetObjectiveLabels[scopeFilter.value] ?? scopeFilter.value}`;
        }
        default:
          return `${scopeFilter.kind} ${scopeFilter.value}`;
      }
    })
    .join(", ");

  const campaignFlowItems = useMemo<VariantFlowItem[]>(() => {
    if (!campaigns) return [];

    return campaigns.map(campaign => ({
      key: `campaign-${campaign.id}`,
      title: campaignNameMap[campaign.campaign_id.toString()] || `Campaign ${campaign.campaign_id}`,
      subtitle: `ID: ${campaign.campaign_id}`,
      campaignID: campaign.campaign_id,
      controlCampaignID: campaign.control_campaign_id,
      testGroup: campaign.test_group,
      variant: campaign.variant,
      trafficAllocation: campaign.traffic_allocation,
      params: toCampaignDisplayParams(campaign.params),
    }));
  }, [campaignNameMap, campaigns]);

  const customCampaignGroups = useMemo(() => {
    const controls = campaignFlowItems.filter(item => item.variant === "control" && item.campaignID);
    const tests = campaignFlowItems.filter(item => item.variant === "test");
    const controlIDs = new Set(controls.map(item => item.campaignID));

    return {
      groups: controls.map(control => ({
        key: control.key,
        control,
        tests: tests.filter(test => test.controlCampaignID === control.campaignID),
      })),
      unassignedTests: tests.filter(test => !test.controlCampaignID || !controlIDs.has(test.controlCampaignID)),
    };
  }, [campaignFlowItems]);

  const predictionFlightFlowItems = useMemo<VariantFlowItem[]>(() => {
    if (!experiment.prediction_flights) return [];

    return experiment.prediction_flights.map(predictionFlight => ({
      key: `prediction-flight-${predictionFlight.id}`,
      title:
        predictionFlightNameMap[predictionFlight.prediction_flight_id.toString()] ||
        `Prediction Flight ${predictionFlight.prediction_flight_id}`,
      subtitle: `ID: ${predictionFlight.prediction_flight_id}`,
      variant: predictionFlight.variant,
      trafficAllocation: predictionFlight.traffic_allocation,
      params: toPredictionFlightDisplayParams(predictionFlight.params),
      predictorSources:
        importedPredictionFlightMetadataMap[
          predictionFlight.prediction_flight_id.toString()
        ]?.predictor_configs?.filter(
          predictorConfig => predictorConfig.repo?.trim() || predictorConfig.branch?.trim(),
        ) || [],
    }));
  }, [experiment.prediction_flights, importedPredictionFlightMetadataMap, predictionFlightNameMap]);

  const getExperimentTypeName = (type?: ExperimentType) => {
    if (type === ExperimentType.Global) return "Global";
    return "Custom";
  };

  return (
    <div className={cn("lg:grid lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-6")}>
      <div className="space-y-10">
        {/* Overview */}
        <section>
          <header className="mb-4 border-b border-border pb-2">
            <h3 className="text-lg font-semibold tracking-tight">Overview</h3>
          </header>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-x-6 gap-y-3">
            <div>
              <div className="text-xs text-muted-foreground">Type</div>
              <div className="font-medium text-sm">{getExperimentTypeName(experiment.type)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Owner</div>
              <div className="font-medium text-sm">
                {userMap[experiment.owner] || experiment.owner_user?.name || "-"}
              </div>
            </div>
            {experiment.type === ExperimentType.Custom && (
              <div>
                <div className="text-xs text-muted-foreground">
                  {experiment.audiences.length > 1 ? "Audiences" : "Audience"}
                </div>
                <div className="font-medium text-sm">
                  {experiment.audiences.length > 0
                    ? experiment.audiences.map(id => audienceNameMap[id.toString()] || id).join(", ")
                    : "-"}
                </div>
              </div>
            )}
            {experiment.type === ExperimentType.Global &&
              experiment.scope_filters &&
              experiment.scope_filters.length > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground">Scope</div>
                  <div className="font-medium text-sm">{scopeSummary}</div>
                </div>
              )}
            <div>
              <div className="text-xs text-muted-foreground">Start</div>
              <div className="font-medium text-sm">{formatUTCDateTime(experiment.start_date)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">End</div>
              <div className="font-medium text-sm">{formatUTCDateTime(experiment.end_date)}</div>
            </div>
          </div>
        </section>

        {/* Hypothesis */}
        <section>
          <header className="mb-4 border-b border-border pb-2">
            <h3 className="text-lg font-semibold tracking-tight">Hypothesis</h3>
          </header>
          {experiment.hypothesis ? (
            <Editor value={experiment.hypothesis} readOnly experimentId={experiment.id?.toString()} />
          ) : (
            <p className="text-sm text-muted-foreground">No hypothesis provided.</p>
          )}
        </section>

        {/* Campaigns (for Custom experiments) */}
        {experiment.type === ExperimentType.Custom && campaignFlowItems.length > 0 && (
          <section>
            <header className="mb-4 border-b border-border pb-2">
              <h3 className="text-lg font-semibold tracking-tight">Campaigns</h3>
            </header>
            <CustomCampaignGroups
              groups={customCampaignGroups.groups}
              unassignedTests={customCampaignGroups.unassignedTests}
            />
          </section>
        )}

        {/* Prediction Flights (for Global experiments) */}
        {experiment.type === ExperimentType.Global && predictionFlightFlowItems.length > 0 && (
          <section>
            <header className="mb-4 border-b border-border pb-2">
              <h3 className="text-lg font-semibold tracking-tight">Prediction Flights</h3>
            </header>
            <VariantOverviewTable items={predictionFlightFlowItems} />
          </section>
        )}
      </div>

      <aside className="mt-6 lg:mt-0 lg:sticky lg:top-6 lg:self-start">
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Links</h3>
          <div className="mt-3 space-y-2">
            {externalLinks.length > 0 ? (
              externalLinks.map((link, index) => (
                <a
                  key={index}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline break-all"
                >
                  <LinkIcon kind={getLinkIconKind(link)} />
                  <span>{link}</span>
                </a>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No links added.</p>
            )}
          </div>
        </section>
      </aside>
    </div>
  );
}
